import React, { useState } from 'react';
import { CircularProgress, Box, Alert } from '@mui/material';
import { TableWithActions } from '../../../layouts/components/TableWithActions';
import { useConfirmation } from '../confirmation';
import { DataManagementConfig, extractHookData, extractMutationData, TableAction } from './types';

interface DataManagementTabProps<TData, TCreateData, TUpdateData> {
  config: DataManagementConfig<TData, TCreateData, TUpdateData> & {
    paginationHook?: () => {
      currentPage: number;
      pageSize: number;
      onPageChange: (page: number, size: number) => void;
      queryResult: any;
      searchQuery?: string;
      onSearchChange?: (searchQuery: string) => void;
    };
  };
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function DataManagementTab<TData, TCreateData, TUpdateData>({
  config,
  onRefresh,
  isRefreshing,
}: DataManagementTabProps<TData, TCreateData, TUpdateData>) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TData | null>(null);

  const { confirm } = useConfirmation();

  // Handle server-side pagination if configured
  const paginationData = config.paginationHook?.();
  const queryResult = paginationData?.queryResult || config.hooks.useQuery();

  // Note: useCreate hook is called here to ensure proper React hook ordering
  // The actual create logic is handled by the dialog component
  config.hooks.useCreate?.();
  const updateResult = config.hooks.useUpdate?.();
  const deleteResult = config.hooks.useDelete?.();

  const { data, isLoading, error, totalCount, isFetching } = extractHookData(queryResult);
  const updateMutation = extractMutationData(updateResult);
  const deleteMutation = extractMutationData(deleteResult);

  // Extract refetch function from query result (if available)
  const refetch = queryResult?.refetch;
  const canRefresh = typeof refetch === 'function';

  const handleCreate = () => {
    setEditingItem(null);
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (item: TData) => {
    setEditingItem(item);
    setIsCreateDialogOpen(true);
  };

  const handleDelete = async (item: TData) => {
    if (!config.actions?.delete || !deleteMutation) return;

    const confirmed = await confirm({
      title: config.actions.delete.confirmTitle,
      message: config.actions.delete.confirmMessage(item),
      confirmText: config.actions.delete.label,
      confirmButtonColor: 'error',
    });

    if (confirmed) {
      try {
        // Use custom transform function if provided, otherwise use item.id
        const deleteParams = config.actions.delete.transformParams
          ? config.actions.delete.transformParams(item)
          : (item as any).id;
        await deleteMutation.mutateAsync(deleteParams);
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingItem(null);
  };

  // Only show full loading spinner on initial load (when data is undefined, not just empty)
  if (isLoading && data === undefined) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {config.table.errorMessage || 'Failed to load data. Please try refreshing the page.'}
        </Alert>
      </Box>
    );
  }

  const tableActions: any[] = [];

  if (config.actions?.create) {
    tableActions.push({
      label: config.actions.create.label,
      onClick: handleCreate,
      variant: 'outlined',
      color: 'primary',
    });
  }

  const rowActions: TableAction<TData>[] = [];

  if (config.actions?.edit && updateMutation) {
    rowActions.push({
      label: config.actions.edit.label,
      onClick: handleEdit,
    });
  }

  if (config.actions?.delete && deleteMutation) {
    rowActions.push({
      label: config.actions.delete.label,
      onClick: handleDelete,
    });
  }

  if (config.actions?.custom) {
    rowActions.push(...config.actions.custom);
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TableWithActions
        hasSearch={config.features?.search ?? true}
        hasPagination={config.features?.pagination ?? true}
        hasActions={tableActions.length > 0}
        actions={tableActions}
        rowActions={rowActions}
        data={data}
        columns={config.table.columns}
        onRowClick={config.features?.onRowClick}
        // Server-side pagination props
        serverSide={config.features?.serverSidePagination ?? false}
        totalCount={totalCount}
        currentPage={paginationData?.currentPage ?? 0}
        pageSize={paginationData?.pageSize ?? 10}
        onPageChange={paginationData?.onPageChange}
        // Server-side search props
        searchTerm={paginationData?.searchQuery}
        onSearchChange={paginationData?.onSearchChange}
        // Loading state props
        isLoading={isFetching}
        showSkeletonRows={true}
        // Empty state props
        emptyMessage={config.table.emptyMessage}
        // Refresh props - use prop if provided, otherwise use internal refetch
        onRefresh={onRefresh || (canRefresh ? refetch : undefined)}
        isRefreshing={isRefreshing ?? isFetching}
      />

      {config.actions?.create && (
        <config.actions.create.dialog
          open={isCreateDialogOpen}
          onClose={handleCloseDialog}
          editingItem={editingItem}
          onSuccess={handleCloseDialog}
        />
      )}
    </Box>
  );
}
